// src/api/axiosConfig.js
import axios from 'axios';

// Sanitize API base URL
const rawBase = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/pongo';
const API_BASE = rawBase.split(',')[0].trim().replace(/\/+$/, '');

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // ← Critical: send session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// CSRF token management
let csrfToken = null;

export const getCSRFToken = async () => {
  if (csrfToken) return csrfToken;
  
  try {
    // Use axios (not fetch) to share cookies/session
    const res = await api.get('/get-csrf/', { 
      timeout: 5000,
      // Don't attach CSRF to this request (chicken-egg problem)
      headers: { 'X-CSRFToken': undefined }
    });
    
    if (res.data?.csrfToken) {
      csrfToken = res.data.csrfToken;
      // Set as default for future requests
      api.defaults.headers.common['X-CSRFToken'] = csrfToken;
    }
    return csrfToken;
  } catch (err) {
    console.warn('CSRF fetch failed (non-critical):', err.message);
    return null;
  }
};

// Initialize CSRF on module load (browser only)
if (typeof window !== 'undefined') {
  getCSRFToken().catch(() => {});
}

// Request interceptor: ensure CSRF token is attached
api.interceptors.request.use(
  async (config) => {
    // Only attach CSRF to state-changing methods
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      // If token not loaded yet, try to fetch it
      if (!csrfToken) {
        await getCSRFToken();
      }
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle auth errors gracefully
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      console.warn('⚠️ 403 Forbidden - check: 1) User logged in, 2) CSRF token valid, 3) Session cookies sent');
      console.warn('Request:', error.config?.method?.toUpperCase(), error.config?.url);
    }
    if (error.code === 'ERR_SSL_PROTOCOL_ERROR' || error.message?.includes('SSL')) {
      console.error('🔐 SSL error - verify backend URL');
    }
    return Promise.reject(error);
  }
);

export default api;